/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Injectable,
  OnDestroy,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    clarity?: ClarityFunction;
  }
}

/**
 * Type definition for the Clarity function
 *
 * This type represents the Microsoft Clarity global function that is available
 * on the window object after the Clarity script is loaded. It's used to interact
 * with the Clarity API for tracking events, identifying users, and configuring settings.
 *
 * @example
 * ```typescript
 * // Using the ClarityFunction type
 * const clarity: ClarityFunction = window.clarity;
 * clarity('event', 'button_click', { button_id: 'submit' });
 * ```
 */
export type ClarityFunction = (event: string, ...args: any[]) => void;

/**
 * Interface for Clarity metadata
 *
 * This interface defines the structure of metadata returned by Clarity when requested.
 * It contains information about the current session, user, and project that can be
 * used for analytics and tracking purposes.
 *
 * @example
 * ```typescript
 * // Example of using ClarityMetadata
 * const metadata: ClarityMetadata = {
 *   userId: 'user123',
 *   sessionId: 'session456',
 *   projectId: 'project789'
 * };
 * ```
 */
export interface ClarityMetadata {
  /** The unique identifier for the current user */
  userId?: string;
  /** The unique identifier for the current session */
  sessionId?: string;
  /** The project ID associated with this Clarity instance */
  projectId?: string;
}

/**
 * Interface for Clarity configuration
 *
 * This interface defines the configuration options for the ClarityService.
 * It allows customization of the Clarity integration, including the project ID
 * and whether tracking is enabled.
 *
 * @example
 * ```typescript
 * // Example of configuring Clarity
 * const config: ClarityConfig = {
 *   projectId: 'your_clarity_project_id',
 *   enabled: true
 * };
 * ```
 */
export interface ClarityConfig {
  /** The Microsoft Clarity project ID (required) */
  projectId: string;
  /** Whether Clarity tracking is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Interface for user identification parameters
 *
 * This interface defines the parameters used to identify a user in Clarity.
 * User identification allows you to track user behavior across sessions and
 * associate analytics data with specific users.
 *
 * @example
 * ```typescript
 * // Example of identifying a user
 * const identifyParams: ClarityIdentifyParams = {
 *   userId: 'user123',
 *   sessionId: 'session456',
 *   pageId: 'page789',
 *   friendlyName: 'John Doe'
 * };
 * ```
 */
export interface ClarityIdentifyParams {
  /** The unique identifier for the user (required) */
  userId: string;
  /** The unique identifier for the current session (optional) */
  sessionId?: string;
  /** The unique identifier for the current page (optional) */
  pageId?: string;
  /** A human-readable name for the user (optional) */
  friendlyName?: string;
}

/**
 * Interface for event tracking parameters
 *
 * This interface defines the parameters used to track custom events in Clarity.
 * Custom events allow you to track specific user interactions and behaviors
 * that are important for your application.
 *
 * @example
 * ```typescript
 * // Example of tracking a custom event
 * const eventParams: ClarityEventParams = {
 *   eventName: 'button_click',
 *   data: {
 *     button_id: 'submit',
 *     page: 'checkout'
 *   }
 * };
 * ```
 */
export interface ClarityEventParams {
  /** The name of the event to track (required) */
  eventName: string;
  /** Additional data to associate with the event (optional) */
  data?: Record<string, any>;
}

/**
 * Enum for Clarity loading states
 *
 * This enum represents the different states of the Clarity script loading process.
 * It helps track whether Clarity has been successfully loaded and is ready to use.
 *
 * @example
 * ```typescript
 * // Example of checking loading state
 * if (clarityService.getLoadingState() === ClarityLoadingState.LOADED) {
 *   clarityService.trackEvent('page_view');
 * }
 * ```
 */
export enum ClarityLoadingState {
  /** Clarity has not been loaded yet */
  NOT_LOADED = 'NOT_LOADED',
  /** Clarity is currently loading */
  LOADING = 'LOADING',
  /** Clarity has been successfully loaded and is ready to use */
  LOADED = 'LOADED',
  /** An error occurred while loading Clarity */
  ERROR = 'ERROR',
}

/**
 * Enum for Clarity consent states
 *
 * This enum represents the different consent states for user tracking.
 * It's used to manage user privacy preferences and comply with regulations
 * like GDPR and CCPA.
 *
 * @example
 * ```typescript
 * // Example of setting user consent
 * clarityService.setUserConsent(ClarityConsentState.GRANTED);
 * ```
 */
export enum ClarityConsentState {
  /** User has granted consent for tracking */
  GRANTED = 'granted',
  /** User has denied consent for tracking */
  DENIED = 'denied',
}

/**
 * Service for managing Microsoft Clarity analytics integration
 *
 * The ClarityService provides a comprehensive interface for integrating Microsoft Clarity
 * into your Angular application. It handles script injection, event tracking, user identification,
 * and privacy management.
 *
 * ## Features
 * - Automatic script injection and initialization
 * - Event tracking with custom data
 * - User identification across sessions
 * - Privacy controls and consent management
 * - Error handling and state management
 * - Cleanup and resource management
 *
 * ## Basic Usage
 *
 * ```typescript
 * import { Component, OnInit } from '@angular/core';
 * import { ClarityService } from './services/clarity.service';
 *
 * @Component({
 *   selector: 'app-example',
 *   template: `...`
 * })
 * export class ExampleComponent implements OnInit {
 *   constructor(private clarityService: ClarityService) {}
 *
 *   ngOnInit() {
 *     // Initialize Clarity
 *     this.clarityService.init();
 *
 *     // Track a custom event
 *     this.clarityService.trackEvent('page_view', { page: 'home' });
 *
 *     // Identify the current user
 *     this.clarityService.identify('user123', undefined, undefined, 'John Doe');
 *   }
 * }
 * ```
 *
 * ## Privacy and Compliance
 *
 * The service includes features to help with privacy compliance:
 * - Consent management for tracking
 * - Complete data clearing capabilities
 * - Enable/disable functionality
 *
 * ```typescript
 * // Set user consent
 * this.clarityService.setUserConsent(ClarityConsentState.GRANTED);
 *
 * // Clear all user data
 * this.clarityService.clearUser();
 *
 * // Disable tracking entirely
 * this.clarityService.setEnabled(false);
 * ```
 *
 * ## Error Handling
 *
 * The service includes comprehensive error handling. All methods will gracefully
 * handle cases where Clarity is not available or encounters errors:
 *
 * ```typescript
 * // Methods will not throw errors but will log warnings
 * this.clarityService.trackEvent('event_name'); // Will warn if Clarity is not loaded
 * ```
 *
 * @see {@link https://clarity.microsoft.com/} Microsoft Clarity Documentation
 */
@Injectable({
  providedIn: 'root',
})
export class ClarityService implements OnDestroy {
  private readonly renderer: Renderer2;
  private loadingState: ClarityLoadingState = ClarityLoadingState.NOT_LOADED;
  private config: ClarityConfig;
  private scriptElement: HTMLScriptElement | null = null;
  private readonly CLARITY_SCRIPT_URL = 'https://www.clarity.ms/tag/';

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.config = {
      projectId: environment.CLARITY_PROJECT_ID || '',
      enabled: true,
    };
  }

  /**
   * Initialize Microsoft Clarity with the project ID from environment
   *
   * This method loads the Microsoft Clarity script and initializes it with the
   * configured project ID. It should be called once when your application starts.
   * The method will only proceed if Clarity is enabled and a valid project ID is provided.
   *
   * @throws Will handle errors internally and update the loading state to ERROR
   *
   * @example
   * ```typescript
   * // In your app component or app initialization
   * constructor(private clarityService: ClarityService) {}
   *
   * ngOnInit() {
   *   this.clarityService.init();
   * }
   * ```
   */
  init(): void {
    try {
      // Check if initialization should proceed
      if (!this.shouldInitialize()) {
        return;
      }

      // Update state to indicate loading has started
      this.loadingState = ClarityLoadingState.LOADING;
      this.injectClarityScript();
      this.loadingState = ClarityLoadingState.LOADED;
      console.log(
        `Microsoft Clarity initialized with project ID: ${this.config.projectId}`,
      );
    } catch (error) {
      // Handle any errors during initialization
      this.handleError('Failed to initialize Clarity', error);
      this.loadingState = ClarityLoadingState.ERROR;
    }
  }

  /**
   * Check if Clarity should be initialized
   *
   * This private method validates the preconditions for initializing Clarity.
   * It checks if Clarity is enabled, a valid project ID is provided, and
   * Clarity hasn't been loaded already.
   *
   * @returns True if Clarity should be initialized, false otherwise
   */
  private shouldInitialize(): boolean {
    return !!(
      this.config.enabled &&
      this.config.projectId &&
      // Prevent initialization with placeholder project ID
      this.config.projectId !== 'YOUR_CLARITY_PROJECT_ID' &&
      this.loadingState === ClarityLoadingState.NOT_LOADED
    );
  }

  /**
   * Inject the Clarity script into the DOM
   *
   * This private method creates and injects the Clarity script into the DOM
   * using the official Microsoft Clarity installation pattern.
   */
  private injectClarityScript(): void {
    // Create the Clarity script injection function
    const clarityScript = this.createClarityScriptFunction();
    clarityScript(window, document, 'clarity', 'script', this.config.projectId);
  }

  /**
   * Create the Clarity script injection function
   *
   * This private method creates a function that follows the official Microsoft Clarity
   * installation pattern. It sets up the Clarity queue, loads the script, and
   * manages the initialization process.
   *
   * @returns A function that injects the Clarity script when called
   */
  private createClarityScriptFunction(): (
    c: any,
    l: Document,
    a: string,
    r: string,
    i: string,
  ) => void {
    return (c: any, l: Document, a: string, r: string, i: string) => {
      this.initializeClarityQueue(c, a);
      this.createAndInjectScript(l, r, i);
    };
  }

  /**
   * Initialize the Clarity queue for command buffering
   *
   * This private method sets up the Clarity queue that stores commands
   * until the Clarity script is fully loaded.
   *
   * @param c The window context
   * @param a The clarity function name
   */
  private initializeClarityQueue(c: any, a: string): void {
    // Initialize the clarity queue if it doesn't exist
    // This queue stores commands until the Clarity script is fully loaded
    c[a] =
      c[a] ||
      function (...args: any[]) {
        (c[a].q = c[a].q || []).push(args);
      };

    // Set the load timestamp for debugging purposes
    c[a].l = new Date().getTime();
  }

  /**
   * Create and inject the Clarity script element
   *
   * This private method creates the script element and injects it into the DOM.
   *
   * @param l The document
   * @param r The tag name to find (usually 'script')
   * @param i The project ID
   */
  private createAndInjectScript(l: Document, r: string, i: string): void {
    // Create and inject the script element using Renderer2 for Angular compatibility
    const scriptElement: HTMLScriptElement =
      this.renderer.createElement('script');
    this.renderer.setProperty(scriptElement, 'async', true);
    this.renderer.setProperty(
      scriptElement,
      'src',
      `${this.CLARITY_SCRIPT_URL}${i}`,
    );

    // Find the first script element to insert before it
    // This ensures the script loads early in the page lifecycle
    const firstScript = l.getElementsByTagName(r)[0];
    this.renderer.insertBefore(
      firstScript.parentNode,
      scriptElement,
      firstScript,
    );

    // Store reference for cleanup when the service is destroyed
    this.scriptElement = scriptElement;
  }

  /**
   * Check if Clarity is loaded and available
   *
   * This method checks if the Microsoft Clarity script has been successfully loaded
   * and is ready to use. It verifies both the presence of the clarity function
   * and that the service is in the LOADED state.
   *
   * @returns True if Clarity is available, false otherwise
   *
   * @example
   * ```typescript
   * if (this.clarityService.isClarityAvailable()) {
   *   this.clarityService.trackEvent('button_click');
   * }
   * ```
   */
  isClarityAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.clarity === 'function' &&
      this.loadingState === ClarityLoadingState.LOADED
    );
  }

  /**
   * Track a custom event with Clarity
   *
   * This method records a custom event in Microsoft Clarity, which can be used
   * to track specific user interactions and behaviors. Optional data can be
   * attached to the event for additional context.
   *
   * @param eventName The name of the event to track
   * @param data Optional additional data to associate with the event
   *
   * @example
   * ```typescript
   * // Track a simple event
   * this.clarityService.trackEvent('button_click');
   *
   * // Track an event with additional data
   * this.clarityService.trackEvent('purchase', {
   *   product_id: 'prod123',
   *   amount: 99.99,
   *   currency: 'USD'
   * });
   * ```
   */
  trackEvent(eventName: string, data?: Record<string, any>): void {
    try {
      if (!this.isClarityAvailable()) {
        console.warn('Clarity is not available for event tracking');
        return;
      }

      // Track the event with or without additional data
      if (data) {
        window.clarity!('event', eventName, data);
      } else {
        window.clarity!('event', eventName);
      }
    } catch (error) {
      this.handleError(`Failed to track event: ${eventName}`, error);
    }
  }

  /**
   * Track a custom event using the interface-based approach
   *
   * This method provides an alternative way to track events using the
   * ClarityEventParams interface. It's useful when you need to construct
   * event parameters programmatically.
   *
   * @param params The event parameters including name and optional data
   *
   * @example
   * ```typescript
   * const eventParams: ClarityEventParams = {
   *   eventName: 'form_submit',
   *   data: {
   *     form_id: 'contact_form',
   *     success: true
   *   }
   * };
   * this.clarityService.trackEventWithParams(eventParams);
   * ```
   */
  trackEventWithParams(params: ClarityEventParams): void {
    this.trackEvent(params.eventName, params.data);
  }

  /**
   * Identify a user with Clarity
   *
   * This method identifies the current user in Microsoft Clarity, which allows
   * you to track user behavior across sessions and associate analytics data
   * with specific users.
   *
   * @param userId The unique identifier for the user
   * @param sessionId Optional unique identifier for the current session
   * @param pageId Optional unique identifier for the current page
   * @param friendlyName Optional human-readable name for the user
   *
   * @example
   * ```typescript
   * // Identify a user with just the user ID
   * this.clarityService.identify('user123');
   *
   * // Identify a user with additional parameters
   * this.clarityService.identify(
   *   'user123',
   *   'session456',
   *   'page789',
   *   'John Doe'
   * );
   * ```
   */
  identify(
    userId: string,
    sessionId?: string,
    pageId?: string,
    friendlyName?: string,
  ): void {
    try {
      if (!this.isClarityAvailable()) {
        console.warn('Clarity is not available for user identification');
        return;
      }

      window.clarity!('identify', userId, sessionId, pageId, friendlyName);
    } catch (error) {
      this.handleError('Failed to identify user', error);
    }
  }

  /**
   * Identify a user using the interface-based approach
   *
   * This method provides an alternative way to identify users using the
   * ClarityIdentifyParams interface. It's useful when you need to construct
   * identification parameters programmatically.
   *
   * @param params The user identification parameters
   *
   * @example
   * ```typescript
   * const identifyParams: ClarityIdentifyParams = {
   *   userId: 'user123',
   *   friendlyName: 'John Doe'
   * };
   * this.clarityService.identifyWithParams(identifyParams);
   * ```
   */
  identifyWithParams(params: ClarityIdentifyParams): void {
    this.identify(
      params.userId,
      params.sessionId,
      params.pageId,
      params.friendlyName,
    );
  }

  /**
   * Set custom tag for the current session
   *
   * This method sets a custom tag for the current Clarity session, which can
   * be used to segment and filter analytics data. Tags are key-value pairs
   * that provide additional context about the session.
   *
   * @param key The tag key
   * @param value The tag value
   *
   * @example
   * ```typescript
   * // Add a user type tag
   * this.clarityService.setTag('user_type', 'premium');
   *
   * // Add a feature tag
   * this.clarityService.setTag('feature_used', 'advanced_search');
   * ```
   */
  setTag(key: string, value: string): void {
    try {
      if (!this.isClarityAvailable()) {
        console.warn('Clarity is not available for setting tags');
        return;
      }

      window.clarity!('set', key, value);
    } catch (error) {
      this.handleError(`Failed to set tag: ${key}`, error);
    }
  }

  /**
   * Set multiple tags at once
   *
   * This method allows you to set multiple tags in a single call, which is
   * more efficient than calling setTag multiple times.
   *
   * @param tags An object containing key-value pairs of tags to set
   *
   * @example
   * ```typescript
   * // Set multiple tags at once
   * this.clarityService.setTags({
   *   user_type: 'premium',
   *   membership_level: 'gold',
   *   registration_source: 'web'
   * });
   * ```
   */
  setTags(tags: Record<string, string>): void {
    try {
      // Iterate through all tags and set each one
      Object.entries(tags).forEach(([key, value]) => {
        this.setTag(key, value);
      });
    } catch (error) {
      this.handleError('Failed to set multiple tags', error);
    }
  }

  /**
   * Get metadata from Clarity
   *
   * This private method retrieves metadata from Microsoft Clarity, including
   * information about the current session, user, and project. It returns a promise
   * that resolves with the metadata object.
   *
   * @returns Promise that resolves with the metadata object
   */
  private async getMetadata(): Promise<ClarityMetadata> {
    return new Promise<ClarityMetadata>((resolve) => {
      try {
        if (!this.isClarityAvailable()) {
          resolve({});
          return;
        }

        // Request metadata from Clarity
        window.clarity!('metadata', (metadata: ClarityMetadata) => {
          resolve(metadata || {});
        });
      } catch (error) {
        this.handleError('Failed to get metadata', error);
        resolve({});
      }
    });
  }

  /**
   * Get the current Clarity session ID
   *
   * This method retrieves the current session ID from Microsoft Clarity.
   * The session ID can be used to correlate user activity across different
   * parts of your application.
   *
   * @returns Promise that resolves with the session ID or empty string if not available
   *
   * @example
   * ```typescript
   * // Get the session ID for logging or correlation
   * const sessionId = await this.clarityService.getSessionId();
   * console.log('Current session ID:', sessionId);
   * ```
   */
  async getSessionId(): Promise<string> {
    try {
      const metadata = await this.getMetadata();
      return metadata.sessionId || '';
    } catch (error) {
      this.handleError('Failed to get session ID', error);
      return '';
    }
  }

  /**
   * Get the current Clarity user ID
   *
   * This method retrieves the current user ID from Microsoft Clarity.
   * The user ID can be used to correlate user activity across different
   * sessions and devices.
   *
   * @returns Promise that resolves with the user ID or empty string if not available
   *
   * @example
   * ```typescript
   * // Get the user ID for logging or correlation
   * const userId = await this.clarityService.getUserId();
   * console.log('Current user ID:', userId);
   * ```
   */
  async getUserId(): Promise<string> {
    try {
      const metadata = await this.getMetadata();
      return metadata.userId || '';
    } catch (error) {
      this.handleError('Failed to get user ID', error);
      return '';
    }
  }

  /**
   * Start or stop heatmaps recording
   *
   * This method controls whether Microsoft Clarity records heatmaps and
   * session recordings. This is useful for implementing privacy controls
   * or user consent management.
   *
   * @param enabled Whether to enable heatmap recording
   *
   * @example
   * ```typescript
   * // Enable heatmap recording
   * this.clarityService.setHeatmapRecording(true);
   *
   * // Disable heatmap recording for privacy
   * this.clarityService.setHeatmapRecording(false);
   * ```
   */
  setHeatmapRecording(enabled: boolean): void {
    try {
      if (!this.isClarityAvailable()) {
        console.warn('Clarity is not available for heatmap recording');
        return;
      }

      // Set the consent state in Clarity
      window.clarity!('consent', enabled);
    } catch (error) {
      this.handleError(`Failed to set heatmap recording: ${enabled}`, error);
    }
  }

  /**
   * Set user consent for tracking
   *
   * This method provides a convenient way to manage user consent for tracking
   * using the ClarityConsentState enum. It internally calls setHeatmapRecording
   * with the appropriate value based on the consent state.
   *
   * @param consent The consent state (GRANTED or DENIED)
   *
   * @example
   * ```typescript
   * // Grant consent for tracking
   * this.clarityService.setUserConsent(ClarityConsentState.GRANTED);
   *
   * // Deny consent for tracking
   * this.clarityService.setUserConsent(ClarityConsentState.DENIED);
   * ```
   */
  setUserConsent(consent: ClarityConsentState): void {
    // Convert consent state to boolean for heatmap recording
    this.setHeatmapRecording(consent === ClarityConsentState.GRANTED);
  }

  /**
   * Pause Microsoft Clarity tracking
   *
   * This method pauses Microsoft Clarity tracking by setting consent to denied
   * for both ad storage and analytics storage. This is useful for implementing
   * privacy controls or when users want to temporarily pause tracking.
   *
   * @example
   * ```typescript
   * // Pause Clarity tracking
   * this.clarityService.pause();
   * ```
   */
  pause(): void {
    try {
      if (!this.isClarityAvailable()) {
        console.warn('Clarity is not available for pausing tracking');
        return;
      }

      // Set consent to denied for both ad and analytics storage
      window.clarity!('consentv2', {
        // eslint-disable-next-line camelcase
        ad_storage: ClarityConsentState.DENIED,
        // eslint-disable-next-line camelcase
        analytics_storage: ClarityConsentState.DENIED,
      });
    } catch (error) {
      this.handleError('Failed to pause Clarity tracking', error);
    }
  }

  /**
   * Resume Microsoft Clarity tracking
   *
   * This method resumes Microsoft Clarity tracking by setting consent to granted
   * for both ad storage and analytics storage. This is useful when users want
   * to resume tracking after it has been paused.
   *
   * @example
   * ```typescript
   * // Resume Clarity tracking
   * this.clarityService.resume();
   * ```
   */
  resume(): void {
    try {
      if (!this.isClarityAvailable()) {
        console.warn('Clarity is not available for resuming tracking');
        return;
      }

      // Set consent to granted for both ad and analytics storage
      window.clarity!('consentv2', {
        // eslint-disable-next-line camelcase
        ad_storage: ClarityConsentState.GRANTED,
        // eslint-disable-next-line camelcase
        analytics_storage: ClarityConsentState.GRANTED,
      });
    } catch (error) {
      this.handleError('Failed to resume Clarity tracking', error);
    }
  }

  /**
   * Clear user identification in Microsoft Clarity
   *
   * This method completely removes the Clarity script and data to ensure
   * a full reset of all Clarity tracking data. This is useful for implementing
   * privacy controls or when users want to opt out of tracking.
   *
   * The method performs the following operations:
   * 1. Removes the Clarity script from the DOM
   * 2. Clears the Clarity object from the window
   * 3. Resets the internal service state
   * 4. Clears all Clarity-related data (cookies and localStorage)
   *
   * @example
   * ```typescript
   * // Clear all user data for privacy compliance
   * this.clarityService.clearUser();
   * ```
   */
  clearUser(): void {
    try {
      // Perform complete cleanup of Clarity data
      this.removeClarityScript();
      this.clearClarityWindowObject();
      this.resetServiceState();
      this.clearClarityData();
    } catch (error) {
      this.handleError('Failed to clear user data', error);
    }
  }

  /**
   * Remove the Clarity script from the DOM using Renderer2
   *
   * This private method removes the Clarity script element from the DOM.
   * It removes both the script element that was created by this service
   * and any other Clarity scripts that might exist in the DOM.
   */
  private removeClarityScript(): void {
    // Remove the script element created by this service
    if (this.scriptElement) {
      this.renderer.removeChild(
        this.scriptElement.parentNode,
        this.scriptElement,
      );
      this.scriptElement = null;
    }

    // Also remove any other Clarity scripts that might exist
    // This ensures complete cleanup even if multiple scripts were injected
    const clarityScripts = document.querySelectorAll(
      'script[src*="clarity.ms"]',
    );
    clarityScripts.forEach((script) => {
      if (script.parentNode) {
        this.renderer.removeChild(script.parentNode, script);
      }
    });
  }

  /**
   * Clear the Clarity object from the window
   *
   * This private method removes the Clarity global object from the window,
   * effectively disabling all Clarity functionality.
   */
  private clearClarityWindowObject(): void {
    if (typeof window !== 'undefined' && window.clarity) {
      delete window.clarity;
    }
  }

  /**
   * Reset the internal service state
   *
   * This private method resets the service's internal state to indicate
   * that Clarity is no longer loaded.
   */
  private resetServiceState(): void {
    this.loadingState = ClarityLoadingState.NOT_LOADED;
  }

  /**
   * Clear all Clarity-related data
   *
   * This private method clears all Clarity-related data stored in the browser,
   * including cookies and localStorage items.
   */
  private clearClarityData(): void {
    this.clearClarityCookies();
    this.clearClarityLocalStorage();
  }

  /**
   * Clear Clarity-related cookies
   *
   * This private method finds and removes all cookies that are related to
   * Clarity. It looks for cookies with names containing 'clarity' or '_cl'.
   */
  private clearClarityCookies(): void {
    try {
      if (typeof document !== 'undefined') {
        const cookies = document.cookie.split(';');
        cookies.forEach((cookie) => {
          const cookieName = cookie.split('=')[0].trim();
          // Check if the cookie is related to Clarity
          if (cookieName.includes('clarity') || cookieName.includes('_cl')) {
            // Set the cookie to expire in the past to delete it
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          }
        });
      }
    } catch (error) {
      this.handleError('Failed to clear Clarity cookies', error);
    }
  }

  /**
   * Clear Clarity-related localStorage items
   *
   * This private method finds and removes all localStorage items that are
   * related to Clarity. It looks for keys containing 'clarity' or '_cl'.
   */
  private clearClarityLocalStorage(): void {
    try {
      if (typeof localStorage !== 'undefined') {
        const keysToRemove: string[] = [];
        // First pass: identify all keys to remove
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('clarity') || key.includes('_cl'))) {
            keysToRemove.push(key);
          }
        }
        // Second pass: remove all identified keys
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      }
    } catch (error) {
      this.handleError('Failed to clear Clarity localStorage', error);
    }
  }

  /**
   * Reload Clarity after it has been cleared
   *
   * This method reinitializes Clarity after it has been cleared. This is useful
   * when you need to reset Clarity with a new configuration or after a user
   * has granted consent.
   *
   * @example
   * ```typescript
   * // Clear and reload Clarity with a new configuration
   * this.clarityService.updateConfig({ projectId: 'new_project_id' });
   * this.clarityService.reloadClarity();
   * ```
   */
  reloadClarity(): void {
    try {
      this.init();
    } catch (error) {
      this.handleError('Failed to reload Clarity', error);
    }
  }

  /**
   * Stop all tracking for privacy purposes
   *
   * This method stops all Clarity tracking and optionally reloads Clarity
   * after clearing the data. This is useful for implementing privacy controls
   * or when users want to opt out of tracking.
   *
   * @param reloadAfterClear Whether to reload Clarity after clearing data (default: false)
   *
   * @example
   * ```typescript
   * // Stop tracking completely
   * this.clarityService.stopTracking();
   *
   * // Stop tracking and reload with a fresh session
   * this.clarityService.stopTracking(true);
   * ```
   */
  stopTracking(reloadAfterClear = false): void {
    try {
      this.clearUser();
      if (reloadAfterClear) {
        this.reloadClarity();
      }
    } catch (error) {
      this.handleError('Failed to stop tracking', error);
    }
  }

  /**
   * Get the current loading state
   *
   * This method returns the current loading state of the Clarity script.
   * It can be used to check if Clarity is ready to use or if there were
   * any errors during initialization.
   *
   * @returns The current loading state
   *
   * @example
   * ```typescript
   * if (this.clarityService.getLoadingState() === ClarityLoadingState.LOADED) {
   *   // Clarity is ready to use
   *   this.clarityService.trackEvent('page_view');
   * }
   * ```
   */
  getLoadingState(): ClarityLoadingState {
    return this.loadingState;
  }

  /**
   * Check if Clarity is enabled
   *
   * This method returns whether Clarity tracking is currently enabled.
   * This is useful for conditionally enabling or disabling tracking
   * based on user preferences or application state.
   *
   * @returns True if Clarity is enabled, false otherwise
   *
   * @example
   * ```typescript
   * if (this.clarityService.isEnabled()) {
   *   this.clarityService.trackEvent('user_action');
   * }
   * ```
   */
  isEnabled(): boolean {
    return !!this.config.enabled;
  }

  /**
   * Enable or disable Clarity
   *
   * This method enables or disables Clarity tracking. When disabled, all
   * tracking data is cleared and no further events will be recorded.
   *
   * @param enabled Whether to enable or disable Clarity
   *
   * @example
   * ```typescript
   * // Disable Clarity
   * this.clarityService.setEnabled(false);
   *
   * // Re-enable Clarity
   * this.clarityService.setEnabled(true);
   * ```
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (!enabled) {
      // Stop all tracking when disabled
      this.stopTracking();
    }
  }

  /**
   * Update the configuration
   *
   * This method updates the Clarity configuration with the provided values.
   * It merges the new configuration with the existing one, allowing you to
   * update specific properties without affecting others.
   *
   * @param config Partial configuration object with properties to update
   *
   * @example
   * ```typescript
   * // Update just the project ID
   * this.clarityService.updateConfig({ projectId: 'new_project_id' });
   *
   * // Update multiple properties
   * this.clarityService.updateConfig({
   *   projectId: 'new_project_id',
   *   enabled: false
   * });
   * ```
   */
  updateConfig(config: Partial<ClarityConfig>): void {
    // Merge the new configuration with the existing one
    this.config = { ...this.config, ...config };
  }

  /**
   * Handle errors consistently
   *
   * This private method provides consistent error handling throughout the service.
   * It logs the error and updates the loading state to ERROR.
   *
   * @param message Error message describing what went wrong
   * @param error The error object or value that was thrown
   */
  private handleError(message: string, error: unknown): void {
    console.error(`ClarityService Error: ${message}`, error);
    this.loadingState = ClarityLoadingState.ERROR;
  }

  /**
   * Cleanup when the service is destroyed
   *
   * This method is called when the Angular service is destroyed. It performs
   * cleanup operations to remove the Clarity script and prevent memory leaks.
   *
   * @example
   * ```typescript
   * // This method is automatically called by Angular when the service is destroyed
   * // No need to call it manually
   * ```
   */
  ngOnDestroy(): void {
    try {
      // Clean up any resources when the service is destroyed
      if (this.scriptElement) {
        this.removeClarityScript();
      }
    } catch (error) {
      this.handleError('Failed to cleanup service', error);
    }
  }
}
